/* Domovoi · the week (W1): pure maths, no database, no DOM. Tested in tests/week.test.js.

   A day counts from 7:00 to 11pm (16h; 112h a week). When either of you has to
   be somewhere by 10am (out of the door, at work at home, something in the
   calendar), the morning before that doesn't count: getting ready isn't time
   together (Ben, 2026-10-23). For each of you, Domovoi works out when you're
   unavailable to the other: out (work and the journey there and back, regulars
   away from home, a trip), or home but busy (working from home, a regular at
   home, a calendar event). Together is the time you're both home and free;
   apart is the rest of the counted day.

   "Usual" is the same sum over the template alone — the usual week and the
   regulars, no check-in changes, no calendar, no plans — so this week can be
   set against it. "On purpose" is together time covered by a plan.

   Times are minutes from midnight; dates are 'YYYY-MM-DD'; weekdays run
   0 = Monday … 6 = Sunday, as in usual_week, work_places.days and regulars.days. */

export const WAKE_START = 7 * 60;
export const WAKE_END = 23 * 60;
export const WAKE_MINUTES = WAKE_END - WAKE_START;      // 960 = 16h
export const MORNING_CUTOFF = 10 * 60;                  // somewhere to be before this: the morning doesn't count
/* The day's shape (DOM-2, Kirsten, 28 Oct: "it doesn't include sleep hours, so how does it know?"). The counted
   frame stays 7am–11pm; inside it, the time before you're up and after you're in bed isn't yours to share, and
   getting ready and meals at home are together but taken: never free. profiles.up_weekday/up_weekend (weekdays,
   Saturday and Sunday), bed_weekday/bed_weekend (Sunday to Thursday nights, Friday and Saturday nights),
   ready_minutes; couples.meals { breakfast, lunch, dinner: { at?, minutes } | false }. Unset: these. */
export const DEFAULT_UP = 7 * 60, DEFAULT_BED = 23 * 60, DEFAULT_READY = 30;
export const DEFAULT_MEALS = { breakfast: { minutes: 30 }, lunch: { at: '12:30', minutes: 45 }, dinner: { at: '18:30', minutes: 45 } };
export const MEALS = ['breakfast', 'lunch', 'dinner'];
/** When you're up on weekday `wd` (0 = Monday): Saturday and Sunday are the weekend's, which falls back to the weekday's. */
export function upAt(p, wd) {
  p = p || {};
  const v = wd >= 5 ? (mins(p.up_weekend) ?? mins(p.up_weekday)) : mins(p.up_weekday);
  return v ?? DEFAULT_UP;
}
/** When you're in bed on the night of weekday `wd`: Friday and Saturday nights are the weekend's. Midnight or later
    (a time before noon) is the end of the counted day. */
export function bedAt(p, wd) {
  p = p || {};
  let v = wd === 4 || wd === 5 ? (mins(p.bed_weekend) ?? mins(p.bed_weekday)) : mins(p.bed_weekday);
  if (v == null) return DEFAULT_BED;
  if (v < 12 * 60) v += 24 * 60;
  return v;
}
/** The household's meals, each { at, minutes } in minutes or null (none): unset ones are DEFAULT_MEALS, and
    breakfast with no time is straight after getting ready. */
export function mealsOf(meals, up, ready) {
  const m = meals && typeof meals === 'object' ? meals : {};
  const out = {};
  for (const k of MEALS) {
    const v = k in m ? m[k] : DEFAULT_MEALS[k];
    if (!v) { out[k] = null; continue; }
    const len = Math.max(5, Math.min(180, Number(v.minutes) || DEFAULT_MEALS[k].minutes));
    const at = mins(v.at) ?? (k === 'breakfast' ? up + ready : mins(DEFAULT_MEALS[k].at));
    out[k] = { at, minutes: len };
  }
  return out;
}
const clock = (m) => (m % (24 * 60) === 0 ? 'midnight' : ampm(m % (24 * 60)));
/** Your day in a line, for setup and Account: "Up at 7am, in bed by 11pm · 30 min to get ready", with the
    weekend's in brackets where it differs ("Up at 6:30am (8am at weekends)"). */
export function dayWords(p) {
  p = p || {};
  const upW = upAt(p, 0), upE = upAt(p, 5), bedW = bedAt(p, 0), bedE = bedAt(p, 4);
  const ready = p.ready_minutes == null ? DEFAULT_READY : Number(p.ready_minutes) || 0;
  const up = 'Up at ' + clock(upW) + (upE !== upW ? ' (' + clock(upE) + ' at weekends)' : '');
  const bed = 'in bed by ' + clock(bedW) + (bedE !== bedW ? ' (' + clock(bedE) + ' on Fridays and Saturdays)' : '');
  return up + ', ' + bed + (ready ? ' · ' + ready + ' min to get ready' : '');
}
/** The household's meals in a line: "Breakfast after getting ready, lunch at 12:30pm, dinner at 6:30pm". */
export function mealsWords(meals) {
  const m = meals && typeof meals === 'object' ? meals : {};
  const parts = [];
  for (const k of MEALS) {
    const v = k in m ? m[k] : DEFAULT_MEALS[k];
    if (!v) continue;
    const at = mins(v.at) ?? (k === 'breakfast' ? null : mins(DEFAULT_MEALS[k].at));
    parts.push((parts.length ? k : k[0].toUpperCase() + k.slice(1)) + (at == null ? ' after getting ready' : ' at ' + ampm(at)));
  }
  return parts.length ? parts.join(', ') : 'None at home';
}
/** One person's day shape on weekday `wd`: { up, bed, asleep: [[s,e]], taken: [[s,e]] } inside the counted day.
    `meals`: the household's (couples.meals; null for the defaults). Undefined — a person built without the
    household, as older code does — takes nothing: no getting ready, no meals. */
export function dayShape(p, wd, meals) {
  p = p || {};
  const up = upAt(p, wd), bed = bedAt(p, wd);
  const ready = Math.max(0, Math.min(180, p.ready_minutes == null ? DEFAULT_READY : Number(p.ready_minutes) || 0));
  const asleep = [];
  if (up > WAKE_START) asleep.push([WAKE_START, Math.min(up, WAKE_END)]);
  if (bed < WAKE_END) asleep.push([Math.max(bed, WAKE_START), WAKE_END]);
  const taken = [];
  if (meals !== undefined) {
    if (ready) taken.push([up, up + ready]);
    const m = mealsOf(meals, up, ready);
    for (const k of MEALS) if (m[k]) taken.push([m[k].at, m[k].at + m[k].minutes]);
  }
  return { up, bed, asleep: union(asleep), taken: minus(union(clip(taken)), asleep) };
}
const DEFAULT_START = 9 * 60, DEFAULT_END = 17 * 60;
const EVENING_START = 17 * 60;   // as regularSlot: from 5pm is the evening
const UNTIMED_PLAN = 120;                               // a plan with no time counts as two hours

// ── Dates ───────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');
export function iso(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
export function parseDate(s) { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); }
export function addDays(s, n) { const d = parseDate(s); d.setDate(d.getDate() + n); return iso(d); }
/** 0 = Monday … 6 = Sunday. */
export function weekday(s) { return (parseDate(s).getDay() + 6) % 7; }
/** The Monday of the week `s` falls in. */
export function weekStart(s) { return addDays(s, -weekday(s)); }
export function weekDates(start, n = 7) { return Array.from({ length: n }, (_, i) => addDays(start, i)); }
/** Whole days from a to b (b − a). */
export function daysBetween(a, b) { return Math.round((parseDate(b) - parseDate(a)) / 86400000); }

// ── Times ───────────────────────────────────────────────────────────────────
/** '08:30' or '08:30:00' → 510. null/undefined → null. */
export function mins(t) {
  if (t == null || t === '') return null;
  const [h, m] = String(t).split(':').map(Number);
  return isFinite(h) ? h * 60 + (m || 0) : null;
}
export function hhmm(m) { return pad(Math.floor(m / 60) % 24) + ':' + pad(m % 60); }
/** 2820 → '47h'; 90 → '1h 30m'; 30 → '30m'. Rounded to the hour at 10h and over. */
export function fmtHours(m) {
  m = Math.max(0, Math.round(m));
  if (m >= 600) return Math.round(m / 60) + 'h';
  const h = Math.floor(m / 60), r = m % 60;
  return h ? (r ? h + 'h ' + r + 'm' : h + 'h') : r ? r + 'm' : '0h';
}

// ── Intervals: sorted, non-overlapping [start, end) pairs ───────────────────
export function clip(list, lo = WAKE_START, hi = WAKE_END) {
  return list.map(([a, b]) => [Math.max(a, lo), Math.min(b, hi)]).filter(([a, b]) => b > a);
}
export function union(list) {
  const s = list.filter(([a, b]) => b > a).slice().sort((x, y) => x[0] - y[0]);
  const out = [];
  for (const [a, b] of s) {
    const last = out[out.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else out.push([a, b]);
  }
  return out;
}
export function total(list) { return union(list).reduce((n, [a, b]) => n + (b - a), 0); }
/** The parts of [lo, hi) not covered by `list`. */
export function gaps(list, lo = WAKE_START, hi = WAKE_END) {
  const out = []; let at = lo;
  for (const [a, b] of union(clip(list, lo, hi))) { if (a > at) out.push([at, a]); at = Math.max(at, b); }
  if (at < hi) out.push([at, hi]);
  return out;
}
export function intersect(x, y) {
  const a = union(x), b = union(y), out = [];
  let i = 0, j = 0;
  while (i < a.length && j < b.length) {
    const lo = Math.max(a[i][0], b[j][0]), hi = Math.min(a[i][1], b[j][1]);
    if (hi > lo) out.push([lo, hi]);
    if (a[i][1] < b[j][1]) i++; else j++;
  }
  return out;
}

// ── One person's template ───────────────────────────────────────────────────
/** The usual day for `wd` from usual_week, falling back to the old profile fields
    (office days, hours, commute) for anyone who hasn't done the new onboarding. */
export function usualDay(person, wd) {
  const row = (person.usual || []).find((r) => r.weekday === wd);
  const p = person.profile || {};
  if (row) return { kind: row.day_kind, placeIds: row.place_ids || [], start: mins(row.day_start), end: mins(row.day_end) };
  if ((p.office_days || []).includes(wd)) return { kind: 'away', placeIds: [], start: null, end: null, legacy: true };
  return { kind: 'free', placeIds: [], start: null, end: null, legacy: true };
}
/** Minutes each way for a day at these places: the longest of them (a day that
    could be either is planned for the further one until the check-in says). */
export function travelFor(person, placeIds, legacy) {
  const places = (person.places || []).filter((pl) => placeIds.includes(pl.id) && pl.kind !== 'home');
  if (places.length) return Math.max(...places.map((pl) => pl.travel_minutes || 0));
  return legacy ? ((person.profile || {}).commute_minutes || 0) : 0;
}

/** One person's day: { out: [[s,e]], busy: [[s,e]], label }. `template` leaves out
    the check-in, the calendar and the partner's private busy times. */
export function personDay(person, date, { template = false } = {}) {
  const wd = weekday(date);
  const p = person.profile || {};
  const base = usualDay(person, wd);
  const ov = template ? null : (person.overrides || []).find((o) => o.on_date === date);
  // A public holiday you get takes the work out of the day, unless the check-in says otherwise.
  const holiday = !template && person.holidays ? person.holidays[date] || null : null;
  const hKind = holiday && !(ov && (ov.day_kind || ov.away_all_day)) && base.kind !== 'free' ? 'free' : null;
  const day = {
    kind: (ov && ov.day_kind) || hKind || base.kind,
    placeIds: (ov && ov.place_ids) || base.placeIds,
    start: (ov && mins(ov.day_start)) ?? base.start ?? mins(p.work_start) ?? DEFAULT_START,
    end: (ov && mins(ov.day_end)) ?? base.end ?? mins(p.work_end) ?? DEFAULT_END,
    legacy: base.legacy && !(ov && ov.day_kind),
  };
  const out = [], busy = [], go = [];
  const shape = dayShape(p, wd, person.meals);
  if (ov && ov.away_all_day) return { out: [[WAKE_START, WAKE_END]], busy: [], kind: 'away', off: false, holiday: null, vacation: false, leaves: WAKE_START, up: shape.up, bed: shape.bed, asleep: [], taken: [] };
  if (day.kind === 'away') {
    const t = travelFor(person, day.placeIds, day.legacy);
    out.push([day.start - t, day.end + t]);
  } else if (day.kind === 'home_busy') {
    busy.push([day.start, day.end]);
    go.push([day.start, day.end]);
  }
  // Evenings: the regulars, unless the check-in says this evening is different.
  const evening = ov && ov.evening_kind;
  if (evening !== 'in' && evening !== 'out') {
    for (const r of person.regulars || []) {
      if (!(r.days || []).includes(wd) || r.together) continue;   // one you do together is a plan for you both (regularPlans)
      const s = mins(r.start_time), e = mins(r.end_time);
      if (s == null || e == null) continue;
      (r.away === false ? busy : out).push([s, e <= s ? WAKE_END : e]);
    }
  } else {
    // The regulars that fall in the day still count; the evening's are replaced.
    for (const r of person.regulars || []) {
      if (!(r.days || []).includes(wd) || r.together) continue;
      const s = mins(r.start_time), e = mins(r.end_time);
      if (s != null && e != null && s < EVENING_START) (r.away === false ? busy : out).push([s, Math.min(e <= s ? WAKE_END : e, EVENING_START)]);
    }
    if (evening === 'out') out.push([19 * 60, 22 * 60 + 30]);
  }
  if (!template) {
    for (const ev of eventsOn(person.events || [], date)) {
      if (ev.with_partner) continue;
      busy.push([ev.start, ev.end]); go.push([ev.start, ev.end]);
    }
    for (const b of eventsOn(person.busy || [], date)) { busy.push([b.start, b.end]); go.push([b.start, b.end]); }
  }
  const off = !!((ov && ov.vacation) || hKind);
  // When you first have somewhere to be: out of the door, at work at home, or in the calendar.
  // A regular at home (yoga in the living room) or running the home doesn't count.
  const starts = out.concat(go).filter(([a, b]) => b > a).map(([a]) => a);
  const leaves = starts.length ? Math.min(...starts) : null;
  const outU = union(clip(out));
  // asleep only where you're not out (an early flight is out, not asleep); taken only at home
  return { out: outU, busy: union(clip(busy)), kind: day.kind, off, holiday: holiday ? holiday.name : null, vacation: !!(ov && ov.vacation), leaves,
    up: shape.up, bed: shape.bed, asleep: minus(shape.asleep, outU), taken: minus(shape.taken, outU) };
}

/** The working window [start, end] in minutes on `date`, or null on a day off or a free day
    (the same rules as personDay: the check-in, then public holidays, then the usual week). */
export function workWindow(person, date) {
  if (!person) return null;
  const p = person.profile || {};
  const base = usualDay(person, weekday(date));
  const ov = (person.overrides || []).find((o) => o.on_date === date);
  if (ov && (ov.away_all_day || ov.vacation)) return null;
  const holiday = person.holidays ? person.holidays[date] : null;
  const kind = (ov && ov.day_kind) || (holiday ? 'free' : base.kind);
  if (kind !== 'away' && kind !== 'home_busy') return null;
  const s = (ov && mins(ov.day_start)) ?? base.start ?? mins(p.work_start) ?? DEFAULT_START;
  const e = (ov && mins(ov.day_end)) ?? base.end ?? mins(p.work_end) ?? DEFAULT_END;
  return [s, e];
}

/** Calendar events as cards on Home (Ben, 2026-10-25): everything outside work hours (evenings,
    weekends, days off, all-day), never a meeting that sits inside the working day. An event you
    both have (same title, same start) or one marked with_partner goes in Planned together; the rest
    are Apart. Your partner's private or hidden ones come from partner_busy as times only (`busy`).
    → { together: Card[], apart: Card[] }, Card { key, event: true, title, dates, all_day, start_time,
    location, mine, both, busy }. */
export function calendarCards({ start, me, partner }) {
  const lo = parseDate(start).getTime(), hi = parseDate(addDays(start, 7)).getTime();
  const cardsOf = (person, mine, rows, busy) => {
    const out = [];
    // "Only count the time": a calendar (time blocks, Kirsten, 28 Oct) or one event that stays off Home
    const quiet = new Set(((person && person.profile) || {}).quiet_calendars || []);
    for (const ev of rows || []) {
      if (ev.quiet || (ev.calendar_name && quiet.has(ev.calendar_name))) continue;
      const s = new Date(ev.starts_at).getTime(), e = new Date(ev.ends_at).getTime();
      if (!(e > s) || !(e > lo && s < hi)) continue;
      const d0 = iso(new Date(Math.max(s, lo)));
      let dates = [d0], startTime = null;
      if (ev.all_day) {
        dates = [];
        for (let d = d0; parseDate(d).getTime() < Math.min(e, hi) && dates.length < 7; d = addDays(d, 1)) dates.push(d);
        if (!dates.length) continue;
      } else {
        const dayStart = parseDate(d0).getTime();
        const a = Math.round((Math.max(s, dayStart) - dayStart) / 60000);
        const b = Math.round((Math.min(e, dayStart + 86400000) - dayStart) / 60000);
        const w = workWindow(person, d0);
        if (w && a >= w[0] && b <= w[1]) continue;
        if (s >= lo) startTime = hhmm(a);
      }
      out.push({
        key: (busy ? 'busy:' : 'ev:') + (ev.id || ev.starts_at), event: true, busy: !!busy, mine, both: !!ev.with_partner,
        title: busy ? '' : String(ev.title || '').trim() || 'Busy', location: busy ? null : ev.location || null,
        dates, all_day: !!ev.all_day, start_time: startTime, starts_at: ev.starts_at,
        norm: busy ? null : String(ev.title || '').trim().toLowerCase(),
        id: busy ? null : ev.id || null, calendar: busy ? null : ev.calendar_name || null,
      });
    }
    return out;
  };
  const mineCards = cardsOf(me, true, me && me.events, false);
  let theirs = partner ? cardsOf(partner, false, partner.events, false).concat(cardsOf(partner, false, partner.busy, true)) : [];
  for (const c of mineCards) {
    const twin = theirs.find((t) => !t.busy && t.norm && t.norm === c.norm && t.starts_at === c.starts_at);
    if (twin) { c.both = true; theirs = theirs.filter((t) => t !== twin); }
  }
  const all = mineCards.concat(theirs);
  const order = (a, b) => (a.dates[0] + (a.start_time || '') < b.dates[0] + (b.start_time || '') ? -1 : 1);
  return { together: all.filter((c) => c.both).sort(order), apart: all.filter((c) => !c.both).sort(order) };
}

/** Timed calendar events (or partner_busy rows) clipped to `date`, in local minutes. All-day ones are left out. */
export function eventsOn(list, date) {
  const dayStart = parseDate(date).getTime(), dayEnd = parseDate(addDays(date, 1)).getTime();
  const out = [];
  for (const ev of list) {
    if (ev.all_day || ev.hidden_from_week) continue;
    const s = new Date(ev.starts_at).getTime(), e = new Date(ev.ends_at).getTime();
    if (!(e > dayStart && s < dayEnd)) continue;
    out.push({ ...ev, start: Math.round((Math.max(s, dayStart) - dayStart) / 60000), end: Math.round((Math.min(e, dayEnd) - dayStart) / 60000) });
  }
  return out;
}

/** Whether someone has told Domovoi about their week (onboarding v2, or at least the old office days). */
export function hasWeek(person) {
  return !!person && ((person.usual || []).length > 0 || ((person.profile || {}).office_days || []).length > 0);
}

// ── Plans ───────────────────────────────────────────────────────────────────
/* Evening plans have no time ("we'll watch it when we want to"): they count for as long as
   they last (an episode, a film; pl.minutes), or two hours, against that day's time together. */
function planIntervals(plans, date) {
  const timed = []; let untimed = 0;
  for (const pl of plans || []) {
    if (pl.on_date !== date || pl.status === 'dropped' || pl.who === 'me') continue;
    if (pl.all_day) { timed.push([WAKE_START, WAKE_END]); continue; }
    const s = mins(pl.start_time), e = mins(pl.end_time);
    if (s != null) timed.push([s, e != null && e > s ? e : s + (pl.minutes || UNTIMED_PLAN)]);
    else untimed += pl.minutes || UNTIMED_PLAN;
  }
  return { timed: union(clip(timed)), untimed };
}

// ── Shows you're watching, in the week ──────────────────────────────────────
/** Episodes out this week of the shows you're watching (household_overview's now_watching):
    [{ id, type, title, date, minutes, releaseNight }], by date. */
export function episodesInWeek(nowWatching, start) {
  const end = addDays(start, 6), out = [];
  for (const s of nowWatching || []) {
    if (s.type !== 'tv' || s.id == null) continue;
    for (const d of new Set([s.last_ep, s.next_air].filter((x) => x && x >= start && x <= end))) {
      out.push({ id: s.id, type: s.type, title: s.title, date: d, minutes: s.runtime || 60, releaseNight: !!s.release_night, finale: s.finale || null });
    }
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}
/** A weekly show you watch together on release day is a plan on each episode's day, unless
    that day already has a plan for it (kept, or dropped to skip this week). */
export function releasePlans(episodes, plans) {
  const has = (e) => (plans || []).some((p) => p.on_date === e.date && p.ref && Number(p.ref.tmdb_id) === e.id);
  return (episodes || []).filter((e) => e.releaseNight && !has(e)).map((e) => ({
    id: 'release:' + e.id + ':' + e.date, title: e.title, on_date: e.date, start_time: null, source: 'curated',
    ref: { tmdb_id: e.id, media_type: e.type, episode: true }, minutes: e.minutes, status: 'planned', release: true,
  }));
}

/** Something every week you do together (regulars.together: Kirsten's jive class, 28 Oct): a plan for you both on
    each of its days, so it counts as time together on purpose, never apart. Made for the week maths and Home's rows,
    never saved. Whoever added it, it's one plan; an evening the check-in has changed, or a day either of you is
    away, drops it. people: [me, partner]; dates: the days to cover. */
export function regularPlans(people, dates) {
  const out = [], seen = new Set();
  const all = (people || []).filter(Boolean);
  for (const person of all) for (const r of person.regulars || []) {
    if (!r.together || seen.has(r.id || r.name)) continue;
    seen.add(r.id || r.name);
    const s = mins(r.start_time), e = mins(r.end_time);
    if (s == null) continue;
    for (const d of dates || []) {
      if (!(r.days || []).includes(weekday(d))) continue;
      const ovs = all.map((p) => (p.overrides || []).find((o) => o.on_date === d)).filter(Boolean);
      if (ovs.some((o) => o.away_all_day) || (s >= EVENING_START && ovs.some((o) => o.evening_kind === 'in' || o.evening_kind === 'out'))) continue;
      out.push({ id: 'regular:' + (r.id || r.name) + ':' + d, title: r.name, on_date: d, start_time: hhmm(s), end_time: e != null && e > s ? hhmm(e) : null,
        who: 'both', status: 'planned', source: 'regular', regular: true, created_by: r.user_id || null });
    }
  }
  return out;
}

// ── Children (DOM-1, 0047) ──────────────────────────────────────────────────────
/* The household's children: when they're home and awake, and who does the school run. `kids`:
   { children: [{ id, care, days, starts, ends, bedtime, drop_by, drop_minutes, pick_by, pick_minutes }],
     away: [{ name, days, on_date, starts, ends, overnight, children }],
     off: { 'YYYY-MM-DD': { name, ids } } }  — days without school (ids: just those children; none is all of them),
   from school.js's schoolOff(). A usual week (`template`) has no days off and no one-off nights away. */
export const DEFAULT_BEDTIME = 19 * 60 + 30;
const offFor = (kids, c, date, template) => {
  if (template) return false;
  const o = ((kids && kids.off) || {})[date];
  return !!o && (!(o.ids || []).length || o.ids.includes(c.id));
};
/** At school or daycare on `date`: [start, end] in minutes, or null (a day off, a weekend, at home). */
export function schoolHours(kids, c, date, { template = false } = {}) {
  if (!c || c.care === 'home' || !(c.days || []).includes(weekday(date)) || offFor(kids, c, date, template)) return null;
  const s = mins(c.starts), e = mins(c.ends);
  return s != null && e != null && e > s ? [s, e] : null;
}
/** When someone else has them on `date` (a child's id): [[s, e]]. Overnight runs to the end of the day and the next
    morning until 10am; no end, to the end of the day. */
export function awayOn(list, childId, date, { template = false } = {}) {
  const out = [];
  const prev = addDays(date, -1);
  for (const a of list || []) {
    if ((a.children || []).length && !a.children.includes(childId)) continue;
    const s = mins(a.starts);
    if (s == null) continue;
    const on = (d) => (a.on_date ? !template && a.on_date === d : (a.days || []).includes(weekday(d)));
    const e = a.overnight ? WAKE_END : mins(a.ends);
    if (on(date)) out.push([s, e != null && e > s ? e : WAKE_END]);
    if (a.overnight && on(prev)) out.push([WAKE_START, 10 * 60]);
  }
  return out;
}
/** When at least one child is home and awake on `date` (minutes, 7am to bedtime, less school and nights away). */
export function kidsHome(kids, date, { template = false } = {}) {
  const ch = (kids && kids.children) || [];
  const out = [];
  for (const c of ch) {
    let home = [[WAKE_START, Math.min(mins(c.bedtime) ?? DEFAULT_BEDTIME, WAKE_END)]];
    const sh = schoolHours(kids, c, date, { template });
    if (sh) home = minus(home, [sh]);
    const aw = awayOn(kids.away, c.id, date, { template });
    if (aw.length) home = minus(home, aw);
    out.push(...home);
  }
  return union(out.filter(([a, b]) => b > a));
}
/** When someone has to be with at least one child on `date` (7am–11pm): at home awake or asleep, so not at school or
    daycare and not with someone else. Going out together needs a sitter for these times. */
export function kidsNeedYou(kids, date) {
  const out = [];
  for (const c of (kids && kids.children) || []) {
    let home = [[WAKE_START, WAKE_END]];
    const sh = schoolHours(kids, c, date);
    if (sh) home = minus(home, [sh]);
    const aw = awayOn(kids.away, c.id, date);
    if (aw.length) home = minus(home, aw);
    out.push(...home);
  }
  return union(out.filter(([a, b]) => b > a));
}
/** For going out without a sitter: per date, the times nobody needs to be with the children ([[a, b]], 7am–11pm),
    and the night away that makes an evening free (the row, or null). → { [date]: { free, away } } */
export function kidFreeDays(kids, dates) {
  const out = {};
  if (!kids || !((kids.children || []).length)) return out;
  for (const date of dates || []) {
    const free = minus([[WAKE_START, WAKE_END]], kidsNeedYou(kids, date));
    // the night away behind a free stretch (that day's, or the morning after the night before)
    const long = free.filter(([x, y]) => y - x >= 120);
    const away = (kids.away || []).find((a) => kids.children.some((c) => awayOn([a], c.id, date)
      .some(([p, q]) => long.some(([x, y]) => p <= x + 30 && q >= Math.min(y, x + 120))))) || null;
    out[date] = { free, away };
  }
  return out;
}

/** The school run for one of you (`uid`) on `date`: out from drop-off less its time to drop-off plus it, and the same
    round pick-up. drop_by/pick_by hold a user id, 'other' or '' per weekday (0 Monday). */
export function schoolRuns(kids, uid, date, { template = false } = {}) {
  if (!uid) return [];
  const wd = weekday(date), out = [];
  for (const c of (kids && kids.children) || []) {
    const sh = schoolHours(kids, c, date, { template });
    if (!sh) continue;
    if ((c.drop_by || [])[wd] === uid) out.push([sh[0] - (c.drop_minutes ?? 15), sh[0] + (c.drop_minutes ?? 15)]);
    if ((c.pick_by || [])[wd] === uid) out.push([sh[1] - (c.pick_minutes ?? 15), sh[1] + (c.pick_minutes ?? 15)]);
  }
  return union(clip(out));
}

// ── The week ────────────────────────────────────────────────────────────────
/** Everything the week view needs.
    input: { start: Monday (or any day: Home's week ahead starts tomorrow), me: Person, partner: Person|null, plans: Plan[] }
    Person: { profile, usual, places, regulars, overrides, events, busy }
    returns { days[7], me, partner, together, onPurpose, apart, usual: { together, apart, meOut, partnerOut }, partnerReady } in minutes. */
export function computeWeek({ start, me, partner, plans, kids = null }) {
  const partnerReady = hasWeek(partner);
  const dates = weekDates(start);
  const A = dates.map((d) => personDay(me, d)), AU = dates.map((d) => personDay(me, d, { template: true }));
  const B = partnerReady ? dates.map((d) => personDay(partner, d)) : null;
  const BU = partnerReady ? dates.map((d) => personDay(partner, d, { template: true })) : null;
  // The school run puts whoever's doing it out for it (and counts as somewhere to be in the morning).
  const runs = (person, days, template) => {
    const uid = person && person.profile && person.profile.id;
    if (!uid || !kids) return;
    days.forEach((d, i) => {
      const r = schoolRuns(kids, uid, dates[i], { template });
      if (!r.length) return;
      d.out = union(clip([...d.out, ...r]));
      d.leaves = d.leaves == null ? r[0][0] : Math.min(d.leaves, r[0][0]);
    });
  };
  runs(me, A, false); runs(me, AU, true);
  if (B) { runs(partner, B, false); runs(partner, BU, true); }
  // Running the home goes in after both days are known (it can fit around the other's).
  const addHome = (days, tdays, h) => days.forEach((d, i) => { d.busy = union([...d.busy, ...h.busy[i]]); tdays[i].busy = union([...tdays[i].busy, ...h.usualBusy[i]]); });
  const hA = homeTime(me.profile, me.regulars, A, AU, B, BU, B ? B.map((d) => d.off) : null);
  const hB = partnerReady ? homeTime(partner.profile, partner.regulars, B, BU, A, AU, A.map((d) => d.off)) : null;
  addHome(A, AU, hA);
  if (hB) addHome(B, BU, hB);
  // A plan just for one of you (a day trip, drinks with a friend) puts them out for its time:
  // all day, or from its time for its length. Untimed and not all day: no way to know, so nothing.
  const soloOut = (person, days) => {
    const id = person && person.profile && person.profile.id;
    if (!id) return;
    for (const pl of plans || []) {
      if (pl.who !== 'me' || pl.status === 'dropped' || pl.created_by !== id) continue;
      const i = dates.indexOf(pl.on_date);
      if (i < 0) continue;
      const s = mins(pl.start_time), e = mins(pl.end_time);
      const span = pl.all_day ? [WAKE_START, WAKE_END] : s != null ? [s, e != null && e > s ? e : s + (pl.minutes || UNTIMED_PLAN)] : null;
      if (span) {
        days[i].out = union(clip([...days[i].out, span]));
        days[i].leaves = days[i].leaves == null ? span[0] : Math.min(days[i].leaves, span[0]);
      }
    }
  };
  soloOut(me, A);
  if (B) soloOut(partner, B);
  const days = [];
  const sum = { together: 0, onPurpose: 0, awake: 0, taken: 0, meOut: 0, partnerOut: 0, usualTogether: 0, usualAwake: 0, usualTaken: 0, usualMeOut: 0, usualPartnerOut: 0, family: 0, usualFamily: 0 };
  const hasKids = !!(kids && (kids.children || []).length);
  dates.forEach((date, i) => {
    const a = A[i], au = AU[i], b = B ? B[i] : null, bu = BU ? BU[i] : null;
    // The day counts from when the first of you is up (or has somewhere to be) to when the last of you is in bed.
    const from = dayFrom(a, b, true), uFrom = dayFrom(au, bu, true);
    const to = dayTo(a, b), uTo = dayTo(au, bu);
    const day = { date, weekday: weekday(date), me: a, partner: b, from, to, together: [], togetherMins: 0, taken: [], takenMins: 0, onPurposeMins: 0, family: [], familyMins: 0, couple: [], coupleMins: 0, kidsHome: hasKids ? kidsHome(kids, date) : [] };
    sum.meOut += total(a.out); sum.usualMeOut += total(au.out);
    if (b) {
      sum.partnerOut += total(b.out); sum.usualPartnerOut += total(bu.out);
      // Together: you're both up, home, and neither of you out or busy. Its stretches include meals (a free day is
      // still "free all day"), but its minutes don't: meals and getting ready are together but taken.
      day.together = intersect(gaps([...a.out, ...a.busy, ...(a.asleep || [])], from, to), gaps([...b.out, ...b.busy, ...(b.asleep || [])], from, to));
      day.taken = intersect(day.together, union([...(a.taken || []), ...(b.taken || [])]));
      day.takenMins = total(day.taken);
      const free = minus(day.together, day.taken);
      day.togetherMins = total(free);
      const { timed, untimed } = planIntervals(plans, date);
      const covered = total(intersect(free, timed));
      day.onPurposeMins = Math.min(day.togetherMins, covered + untimed);
      sum.together += day.togetherMins;
      sum.taken += day.takenMins;
      sum.onPurpose += day.onPurposeMins;
      sum.awake += to - from;
      sum.usualAwake += uTo - uFrom;
      const uAll = intersect(gaps([...au.out, ...au.busy, ...(au.asleep || [])], uFrom, uTo), gaps([...bu.out, ...bu.busy, ...(bu.asleep || [])], uFrom, uTo));
      const uTaken = intersect(uAll, union([...(au.taken || []), ...(bu.taken || [])]));
      const uTogether = minus(uAll, uTaken);
      sum.usualTogether += total(uTogether);
      sum.usualTaken += total(uTaken);
      // Family time: you're both free and the children are home and awake. The rest is just the two of you.
      day.family = hasKids ? intersect(day.together, day.kidsHome) : [];
      day.familyMins = hasKids ? total(minus(day.family, day.taken)) : 0;
      day.couple = hasKids ? minus(day.together, day.kidsHome) : day.together;
      day.coupleMins = day.togetherMins - day.familyMins;
      sum.family += day.familyMins;
      if (hasKids) sum.usualFamily += total(intersect(uTogether, kidsHome(kids, date, { template: true })));
    }
    days.push(day);
  });
  return {
    start, days, partnerReady,
    together: partnerReady ? sum.together : null,
    // With children: family time, and just the two of you (together less family time).
    hasKids,
    family: partnerReady ? sum.family : null,
    couple: partnerReady ? sum.together - sum.family : null,
    onPurpose: partnerReady ? sum.onPurpose : null,
    apart: partnerReady ? sum.awake - sum.together - sum.taken : null,
    // Meals and getting ready while you're both home: together, but not free.
    taken: partnerReady ? sum.taken : null,
    // The hours that count this week: from the first of you up to the last in bed (7am–11pm at most), less the
    // mornings one of you had somewhere to be.
    awake: partnerReady ? sum.awake : null,
    meOut: sum.meOut,
    partnerOut: partnerReady ? sum.partnerOut : null,
    usual: {
      together: partnerReady ? sum.usualTogether : null,
      family: partnerReady ? sum.usualFamily : null,
      couple: partnerReady ? sum.usualTogether - sum.usualFamily : null,
      apart: partnerReady ? sum.usualAwake - sum.usualTogether - sum.usualTaken : null,
      taken: partnerReady ? sum.usualTaken : null,
      awake: partnerReady ? sum.usualAwake : null,
      meOut: sum.usualMeOut,
      partnerOut: partnerReady ? sum.usualPartnerOut : null,
    },
  };
}
/** Where a day starts counting: 7am, or later when either of you has somewhere to be before 10am — and with
    `shape`, no earlier than the first of you is up. */
export function dayFrom(a, b, shape = false) {
  const ls = [a, b].filter((d) => d && d.leaves != null).map((d) => d.leaves);
  const first = ls.length ? Math.min(...ls) : null;
  const base = first != null && first < MORNING_CUTOFF ? Math.min(Math.max(WAKE_START, first), WAKE_END) : WAKE_START;
  if (!shape) return base;
  const ups = [a, b].filter((d) => d && d.up != null).map((d) => d.up);
  return ups.length ? Math.min(WAKE_END, Math.max(base, Math.min(...ups))) : base;
}
/** Where a day stops counting: 11pm, or when the last of you is in bed if that's earlier. */
export function dayTo(a, b) {
  const beds = [a, b].filter((d) => d && d.bed != null).map((d) => d.bed);
  return beds.length ? Math.max(WAKE_START, Math.min(WAKE_END, Math.max(...beds))) : WAKE_END;
}

// ── Running the home ────────────────────────────────────────────────────────
/* Someone who runs the home says about how many hours a week, any set times
   (regulars with kind 'home', busy at home), the hours it usually happens
   between (8am–10pm unless they say), and whether it fits around their
   partner's week. The rest — the hours less the set times — is placed half an
   hour at a time across the days: inside the partner's out-or-busy time
   first, then anywhere in the window. Each day's amount comes from the usual
   week, so a different week moves time within a day, never across days. On a
   day the partner is off (a holiday or vacation), nothing: you're off too. */
export const HOME_FROM = 8 * 60, HOME_TO = 22 * 60;
const SLOT = 30;
/** Minutes a week of set times (regulars with kind 'home'). */
export function homeSetMinutes(regulars) {
  let n = 0;
  for (const r of regulars || []) {
    if (r.kind !== 'home') continue;
    const s = mins(r.start_time), e = mins(r.end_time);
    if (s == null || e == null) continue;
    n += ((e <= s ? WAKE_END : e) - s) * (r.days || []).length;
  }
  return n;
}
/** Set times on weekday `wd`. */
export function homeSetOn(regulars, wd) {
  return union((regulars || []).filter((r) => r.kind === 'home' && (r.days || []).includes(wd))
    .map((r) => [mins(r.start_time), mins(r.end_time) <= mins(r.start_time) ? WAKE_END : mins(r.end_time)]).filter(([a, b]) => a != null && b != null));
}
/** The parts of `x` not covered by `y`. */
export function minus(x, y) { return intersect(x, gaps(y, 0, 24 * 60)); }
function spread(caps, amount) {
  const q = caps.map(() => 0);
  let left = amount;
  while (left > 0) {
    let moved = false;
    for (let d = 0; d < caps.length && left > 0; d++) {
      const room = caps[d] - q[d];
      if (room <= 0) continue;
      const take = Math.min(SLOT, room, left);
      q[d] += take; left -= take; moved = true;
    }
    if (!moved) break;
  }
  return q;
}
function takeFirst(list, amount) {
  const out = []; let left = amount;
  for (const [a, b] of union(list)) {
    if (left <= 0) break;
    const e = Math.min(b, a + left);
    out.push([a, e]); left -= e - a;
  }
  return out;
}
/** Where the flexible part goes. days/tdays: this person's seven days this week and in the usual
    week, without home time; pdays/ptdays: the partner's (null if none); pOff[i]: the partner is off.
    → { busy: intervals[7], usualBusy: intervals[7] } */
export function homeTime(profile, regulars, days, tdays, pdays, ptdays, pOff) {
  const p = profile || {};
  const flex = Math.max(0, (p.home_hours || 0) * 60 - homeSetMinutes(regulars));
  const none = () => days.map(() => []);
  if (!flex) return { busy: none(), usualBusy: none() };
  const lo = mins(p.home_from) ?? HOME_FROM, hi = mins(p.home_to) ?? HOME_TO;
  const follows = p.home_follows !== false && !!ptdays;
  const free = (d) => gaps([...d.out, ...d.busy, ...(d.asleep || [])], lo, hi);
  const theirs = (d) => union([...d.out, ...d.busy]);
  const tFree = tdays.map(free);
  const tFirst = tFree.map((f, i) => (follows ? intersect(f, theirs(ptdays[i])) : f));
  const q1 = spread(tFirst.map(total), flex);
  const placed1 = tFirst.map((f, i) => takeFirst(f, q1[i]));
  const rest = flex - q1.reduce((a, b) => a + b, 0);
  const tSecond = tFree.map((f, i) => minus(f, placed1[i]));
  const q2 = spread(tSecond.map(total), rest);
  const usualBusy = placed1.map((pl, i) => union([...pl, ...takeFirst(tSecond[i], q2[i])]));
  const busy = days.map((d, i) => {
    const quota = q1[i] + q2[i];
    if (!quota || (follows && pOff && pOff[i])) return [];
    const f = free(d);
    const first = follows && pdays ? intersect(f, theirs(pdays[i])) : f;
    const a = takeFirst(first, quota);
    return union([...a, ...takeFirst(minus(f, a), quota - total(a))]);
  });
  return { busy, usualBusy };
}
/** The Running your home preview: a usual week of set times, the rest, and the partner's busy time,
    plus how much of your time together it takes (null without a partner's week). */
export function homePreview(person, partner, monday = '2026-10-26') {
  const dates = weekDates(monday);
  const T = dates.map((d) => personDay(person, d, { template: true }));
  const PT = partner && hasWeek(partner) ? dates.map((d) => personDay(partner, d, { template: true })) : null;
  const h = homeTime(person.profile, person.regulars, T, T, PT, PT, null);
  const set = dates.map((d) => homeSetOn(person.regulars, weekday(d)));
  const lost = PT ? h.usualBusy.reduce((n, b, i) => n + total(intersect(union([...b, ...set[i]]), gaps([...PT[i].out, ...PT[i].busy]))), 0) : null;
  return { days: dates.map((d, i) => ({ partner: PT ? union([...PT[i].out, ...PT[i].busy]) : [], set: set[i], flex: h.usualBusy[i] })), lost };
}

/** Hours out of the house in a usual week (onboarding's running total). */
export function usualOutMinutes(person) {
  let n = 0;
  for (let wd = 0; wd < 7; wd++) n += total(personDay(person, addDays('2026-10-26', wd), { template: true }).out);
  return n;
}

// ── From the work screen to usual_week ──────────────────────────────────────
/** Places (each with days) and hours → the seven usual_week rows.
    No place that day: free. Only Home: working at home. Anywhere else: away. */
export function usualFromPlaces(places, { start = '09:00', end = '17:00' } = {}) {
  return Array.from({ length: 7 }, (_, wd) => {
    const here = places.filter((pl) => (pl.days || []).includes(wd));
    const away = here.filter((pl) => pl.kind !== 'home');
    return {
      weekday: wd,
      day_kind: away.length ? 'away' : here.length ? 'home_busy' : 'free',
      place_ids: (away.length ? away : here).map((pl) => pl.id).filter(Boolean),
      day_start: here.length ? start : null,
      day_end: here.length ? end : null,
    };
  });
}
/** The old profile fields → a first guess at the work screen: Home on the
    weekdays you're not in, plus one "Work" with your office days and commute.
    Only for someone who gave office days: the old setup filled in 9–5 for
    everyone, so hours alone don't mean you work (Olya, 2026-10-23). */
export function placesFromProfile(profile) {
  const p = profile || {};
  const office = (p.office_days || []).slice().sort();
  if (!office.length) return [];
  const out = [{ kind: 'home', name: 'Home', days: [0, 1, 2, 3, 4].filter((d) => !office.includes(d)) }];
  if (office.length) out.push({ kind: 'place', name: 'Work', days: office, travel_mode: p.commute_mode || 'drive', travel_minutes: p.commute_minutes || null });
  return out;
}
/** Where a regular sits in the week grid: 'evening' from 5pm, otherwise 'day'
    (a morning gym or a lunchtime class belongs to the day, not the evening). */
export const EVENING_FROM = 17 * 60;
export function regularSlot(r) { const s = mins(r && r.start_time); return s != null && s >= EVENING_FROM ? 'evening' : 'day'; }

/** Weekdays that appear under more than one place ("Fridays change between places"). */
export function variedDays(places) {
  const n = [0, 0, 0, 0, 0, 0, 0];
  for (const pl of places) for (const d of pl.days || []) n[d] += 1;
  return n.map((c, i) => (c > 1 ? i : -1)).filter((i) => i >= 0);
}

// ── Flags: birthdays and dates coming up ────────────────────────────────────
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WORDS = ['', 'one', 'two', 'three', 'four', 'five', 'six'];
export function longDate(s) { const d = parseDate(s); return DAY_NAMES[weekday(s)] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()]; }
export function shortRange(start) {
  const a = parseDate(start), b = parseDate(addDays(start, 6));
  return a.getMonth() === b.getMonth()
    ? a.getDate() + '–' + b.getDate() + ' ' + MONTHS[a.getMonth()]
    : a.getDate() + ' ' + MONTHS[a.getMonth()] + ' – ' + b.getDate() + ' ' + MONTHS[b.getMonth()];
}
/** "today", "tomorrow", "yesterday", "Saturday" (this week), "next Tuesday", "in two weeks". */
export function whenWords(date, today) {
  const n = daysBetween(today, date);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n === -1) return 'yesterday';
  if (n < 0) return -n + ' days ago';
  if (weekStart(date) === weekStart(today)) return DAY_NAMES[weekday(date)];
  if (n < 14 && weekStart(date) === addDays(weekStart(today), 7)) return 'next ' + DAY_NAMES[weekday(date)];
  const w = Math.round(n / 7);
  return 'in ' + (WORDS[w] || w) + ' weeks';
}
function possessive(name) { return /s$/i.test(name) ? name + '’' : name + '’s'; }
const RELATIVES = { mother: 'mum', father: 'dad', mum: 'mum', dad: 'dad', mom: 'mom' };
function whoName(p) {
  const rel = String(p.relation || '').toLowerCase();
  if (RELATIVES[rel]) return 'Your ' + RELATIVES[rel];
  return p.name;
}

/** Turn gift_upcoming(today − 3, 45) into the week's flags.
    Only people on your side of the family or both (gift_people.side). Everyone else: within the week being looked at (and the three days before it, for
    "was yesterday, not marked bought"). Your partner's birthday and your anniversary:
    up to four weeks out, because those need more notice. Never about you. Never a holiday
    with no one attached. Most urgent first, at most `max`. `end` widens the window past the week
    (Home's Coming up: six weeks); urgent and a message's reminder stay the week from `start`. */
export function giftFlags(upcoming, { today, start, end: until = null, max = 3 } = {}) {
  const occ = (upcoming && upcoming.occasions) || [];
  const meId = upcoming && upcoming.me;
  const weekEnd = addDays(start, 6);
  const end = until && until > weekEnd ? until : weekEnd;
  const out = [];
  for (const o of occ) {
    // Only people on your side, or both sides: Olya isn't asked about Ben's uncle. Not anyone
    // the two of you said "Nothing" or "Not this year" for (gift_person_occasions, shared), or
    // whose reminder you closed (gift_person_prefs.closed, yours alone).
    const year = Number(String(o.date).slice(0, 4));
    const people = (o.people || []).filter((p) => !p.me && (!p.side || !meId || p.side === meId) && p.mode !== 'none' && !p.skip && !p.closed);
    for (const p of people) {
      const isBirthday = o.key === 'birthday', isAnniv = o.key === 'anniversary';
      const close = p.member && (isBirthday || isAnniv);
      const n = daysBetween(today, o.date);
      const inWeek = o.date >= start && o.date <= end;
      const thisWeek = o.date >= start && o.date <= weekEnd;
      const justPast = n < 0 && n >= -3;
      const soon = close && n > 0 && n <= 28;
      if (!(inWeek || justPast || soon)) continue;
      if (n < 0 && !(p.confirmed > p.bought)) continue;          // past and nothing half-done: let it go
      const message = p.mode === 'message' && !p.member;
      // A message, not a gift: only in the week it's in, and done once it's sent.
      if (message && !(thisWeek || (n < 0 && n >= -1))) continue;
      const state = p.bought > 0 ? 'bought' : p.confirmed > 0 ? 'chosen' : p.pencilled > 0 ? 'idea' : 'none';
      if (state === 'bought' && n >= 0 && !message) continue;     // sorted
      const what = isBirthday ? (p.turns && p.turns % 10 === 0 ? ordinal(p.turns) : 'birthday')
        : isAnniv ? 'anniversary' : (o.event_title || prettyKey(o.key));
      const owner = isAnniv ? 'Your' : possessive(whoName(p));
      const when = whenWords(o.date, today);
      const title = n < 0 ? `${owner} ${what} was ${when}`
        : soon && !inWeek ? `${owner} ${what}, ${when}`
        : `${owner} ${what} is ${when}`;
      const detail = message ? 'Send a message.' : state === 'chosen' ? 'Chosen, not marked bought.'
        : state === 'idea' ? (soon && !inWeek ? longDate(o.date) + '. Ideas, nothing chosen.' : 'Ideas, nothing chosen.')
        : (soon && !inWeek ? longDate(o.date) + '. Nothing chosen.' : 'No gift yet.');
      out.push({
        id: o.id + ':' + p.id + ':' + o.date, occasionId: o.id, personId: p.id, occasion: o.key, date: o.date, days: n,
        urgent: thisWeek || justPast, state, title, detail, year, member: !!p.member, mode: p.mode || 'gift',
        name: isAnniv ? null : whoName(p), label: `${owner} ${what}`, what,
        action: message ? 'message' : state === 'chosen' ? 'bought' : state === 'idea' ? 'ideas' : 'find',
        ideas: p.ideas || 0, budget: p.budget_max ? [p.budget_min == null ? null : Math.round(p.budget_min), Math.round(p.budget_max)] : null,
      });
    }
  }
  out.sort((x, y) => (y.urgent - x.urgent) || ((x.days < 0) - (y.days < 0)) * -1 || x.days - y.days);
  return out.slice(0, max);
}
/** Home's Coming up carousel (Ben, 29 Oct: "one for gifts / important upcoming events"): the flags for the next six
    weeks in date order (what's just past first), one card each, except an occasion with three or more people
    (Christmas) is one card for them all. → [{ key, kind: 'flag' | 'group', date, flag | flags, … }] */
export function comingUpCards(flags) {
  const by = new Map();
  for (const f of flags || []) {
    const k = f.occasionId + ':' + f.date;
    if (!by.has(k)) by.set(k, []);
    by.get(k).push(f);
  }
  const out = [];
  for (const [k, list] of by) {
    if (list.length >= 3 && list[0].occasion !== 'birthday' && list[0].occasion !== 'anniversary') {
      const need = list.filter((f) => f.state === 'none' && f.mode !== 'message').length;
      const chosen = list.filter((f) => f.state === 'chosen' || f.state === 'bought').length;
      out.push({ key: 'group:' + k, kind: 'group', date: list[0].date, days: list[0].days, flags: list, occasionId: list[0].occasionId,
        title: String(list[0].what || '').replace(/^./, (c) => c.toUpperCase()), need, chosen, urgent: list.some((f) => f.urgent) });
    } else list.forEach((f) => out.push({ key: 'flag:' + f.id, kind: 'flag', date: f.date, days: f.days, flag: f, urgent: f.urgent }));
  }
  return out.sort((a, b) => a.days - b.days);
}
function ordinal(n) { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); }
function prettyKey(k) { const s = String(k || '').replace(/^event:/, '').replace(/[_-]+/g, ' ').trim(); return s.charAt(0).toUpperCase() + s.slice(1); }

// ── Home, redesigned (the redesign's switch; src/week/home.jsx) ─────────────
/* The week ahead is the seven days after today; tonight has its own card. Each day says how
   much of it you're both free, in words ("Free from 6:30pm"), what fills it for each of you
   ("You're in Markham, then pool. Olya's cooking until 6:30."), and offers one thing to do with
   it: a gift that's due, a show's new episode, a dinner, or a plan for a free day. */

/** 1350 → '10:30pm'; 1320 → '10pm'; 720 → '12pm'. */
export function ampm(m) {
  const h = Math.floor(m / 60) % 24, mm = m % 60;
  return (h % 12 || 12) + (mm ? ':' + pad(mm) : '') + (h >= 12 ? 'pm' : 'am');
}
/** 360, 450 → '6–7:30am'; 1140, 1260 → '7–9pm'; 690, 780 → '11:30am–1pm'. */
export function timeRange(a, b) {
  if (a == null) return '';
  if (b == null || b <= a) return ampm(a);
  const x = ampm(a), y = ampm(b);
  return (x.slice(-2) === y.slice(-2) ? x.slice(0, -2) : x) + '–' + y;
}
const PLURAL_DAYS = ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];
/** [0, 2] → 'Mondays and Wednesdays'; every day; weekdays; weekends. */
export function daysWords(days) {
  const d = [...new Set(days || [])].sort((a, b) => a - b);
  if (d.length === 7) return 'Every day';
  if (d.join() === '0,1,2,3,4') return 'Weekdays';
  if (d.join() === '5,6') return 'Weekends';
  const xs = d.map((x) => PLURAL_DAYS[x]);
  return xs.length < 2 ? xs.join('') : xs.slice(0, -1).join(', ') + ' and ' + xs[xs.length - 1];
}
/** Something every week, read back above Save (so a 6am gym can't quietly become 6pm, Olya 23 Oct):
    "Mondays and Wednesdays, 6–7:30am. Out of the house." / "… With Mira." */
export function regularLine(r, { partnerName, home, kids = [] } = {}) {
  if (!r || !(r.days || []).length || mins(r.start_time) == null) return '';
  const when = daysWords(r.days) + ', ' + timeRange(mins(r.start_time), mins(r.end_time)) + '.';
  if (home) return when;
  // with the children (the family page): "You and Ada, Saturdays 10–11am. Out of the house."
  if (kids.length) return withWords({ together: r.together, partnerName, kids }) + ', ' + when + (r.together && partnerName ? '' : r.away === false ? ' At home.' : ' Out of the house.');
  if (r.together && partnerName) return when + ' With ' + partnerName + '.';
  return when + (r.away === false ? ' At home.' : ' Out of the house.');
}
// ── Something every week with no set time (Kirsten's pickleball, 28 Oct; Ben's ranges, 28 Oct) ──────────────
/* Not at a set time: how many times a week as a range ("2–3 times", "once or twice"), for how long, which days and
   which time of day (morning 7–12, afternoon 12–5, evening 5–11; none is any). Home suggests it in the week ahead's
   free time until the lower number is planned, never past the upper; a plan for it is an ordinary plan with
   ref.fitin (fit_ins, 0044 and 0046). Rows from before 0046 carry only `often` and `fits`: read the old way. */
export const FIT_OFTEN = { once: [1, 1], two: [2, 3], most: [5, 7] };
const FIT_WINDOW = { day: [9 * 60, 17 * 60], evening: [17 * 60, 22 * 60], weekend: [9 * 60, 21 * 60] };
export const PART_WINDOW = { morning: [7 * 60, 12 * 60], afternoon: [12 * 60, 17 * 60], evening: [17 * 60, WAKE_END] };
export const PART_KEYS = ['morning', 'afternoon', 'evening'];
const ranged = (f) => !!f && f.times_min != null;
/** [at least, up to] times a week. */
export function fitRange(f) {
  if (ranged(f)) return [f.times_min, Math.max(f.times_min, f.times_max || f.times_min)];
  return (FIT_OFTEN[f && f.often] || FIT_OFTEN.two).slice();
}
/** The old words for a range, for app bundles that still read `often` (and the database keeps them in step). */
export function oftenOf(min, max) { return max <= 1 ? 'once' : min >= 4 ? 'most' : 'two'; }
/** The old `fits` for days and parts (an older bundle reads them): daytime, evenings, weekends. */
export function fitsOf(days, parts) {
  const d = [...new Set(days || [])].sort().join();
  if (d === '5,6' && !(parts || []).length) return ['weekend'];
  const out = [];
  if ((parts || []).some((p) => p === 'morning' || p === 'afternoon')) out.push('day');
  if ((parts || []).includes('evening')) out.push('evening');
  return out.length === 2 ? [] : out;
}
/** When it fits on `date`, in minutes: its days and time of day (the old shape: daytime and evenings any day, weekends
    on Saturday and Sunday). Takes the row (or, as before, its `fits`). */
export function fitWindow(f, date) {
  const row = Array.isArray(f) ? { fits: f } : f || {};
  const wd = weekday(date);
  if (!ranged(row)) {
    const ks = row.fits && row.fits.length ? row.fits : ['day', 'evening', 'weekend'];
    return union(ks.filter((k) => FIT_WINDOW[k] && (k !== 'weekend' || wd >= 5)).map((k) => FIT_WINDOW[k].slice()));
  }
  if ((row.days || []).length && !row.days.includes(wd)) return [];
  const ps = (row.parts || []).filter((p) => PART_WINDOW[p]);
  return ps.length ? union(ps.map((p) => PART_WINDOW[p].slice())) : [[WAKE_START, WAKE_END]];
}
/** The plans for it between `from` and `to` (not dropped). */
export const fitPlansOf = (f, plans, from, to) => (plans || []).filter((p) => p.status !== 'dropped' && p.ref && p.ref.fitin === f.id && p.on_date >= from && p.on_date < to);
/* The free time for it on one day: together for one with your partner (less what's planned for you both), your
   own for one just for you (less your plans and the shared ones). Today starts at `now`. */
function fitFree(d, plans, f, { me, today, now }) {
  let free;
  if (f.together) {
    if (!d.partner) return [];
    free = minus(union(d.together), plannedSegs(d, plans));
  } else {
    const mine = [];
    for (const p of plans || []) {
      if (p.on_date !== d.date || p.status === 'dropped' || (p.who === 'me' && p.created_by && p.created_by !== me)) continue;
      if (p.all_day) { mine.push([WAKE_START, WAKE_END]); continue; }
      const s = mins(p.start_time), e = mins(p.end_time);
      if (s != null) mine.push([s, e != null && e > s ? e : s + (p.minutes || UNTIMED_PLAN)]);
    }
    free = minus(gaps([...d.me.out, ...d.me.busy, ...(d.me.asleep || []), ...(d.me.taken || [])], d.from == null ? WAKE_START : d.from, d.to ?? WAKE_END), mine);
  }
  if (d.date === today && now != null) free = intersect(free, [[Math.ceil(now / 30) * 30, WAKE_END]]);
  return free.filter(([a, b]) => b > a);
}
/** Where each one could go in `days` (computeWeek's days, today first): on as many days as it's still short of its
    lower number in the seven from `today`, spread out (not the day after another unless it's five or more a week), at
    the earliest stretch that fits its days, time of day and length. Yours just for you, and the ones either of you do
    together. `no`: keys you've said no to ('fitin:<id>:<date>'). → [{ key, fitin, date, from, to, start, all, n }]
    (from–to is the free time it fits in; all, the whole day is free; n, how many are planned this week) */
export function fitInSlots({ fitins = [], days = [], plans = [], me, today, now = null, no = [] }) {
  const out = [];
  const end = addDays(today, 7);
  for (const f of fitins) {
    if (!f.together && f.user_id && f.user_id !== me) continue;
    const [lo, hi] = fitRange(f);
    const done = fitPlansOf(f, plans, today, end);
    const need = Math.min(lo, hi) - done.length;
    if (need <= 0) continue;
    const len = f.minutes || 60;
    const taken = done.map((p) => p.on_date);
    const cands = [];
    for (const d of days) {
      if (d.date < today || d.date >= end || taken.includes(d.date) || no.includes('fitin:' + f.id + ':' + d.date)) continue;
      const win = fitWindow(f, d.date);
      if (!win.length) continue;
      for (const fs of fitFree(d, plans, f, { me, today, now })) {
        const piece = intersect([fs], win).find(([a, b]) => b - Math.ceil(a / 30) * 30 >= len);
        if (!piece) continue;
        const from = d.from == null ? WAKE_START : d.from;
        cands.push({ date: d.date, from: piece[0], to: piece[1], start: Math.ceil(piece[0] / 30) * 30, all: fs[0] <= from + 15 && fs[1] >= (d.to ?? WAKE_END) - 15 });
        break;
      }
    }
    const pick = [];
    const near = (x) => [...taken, ...pick.map((c) => c.date)].some((t) => Math.abs(daysBetween(t, x.date)) < 2);
    if (hi < 5) for (const c of cands) if (pick.length < need && !near(c)) pick.push(c);
    for (const c of cands) if (pick.length < need && !pick.includes(c)) pick.push(c);
    for (const c of pick) out.push({ key: 'fitin:' + f.id + ':' + c.date, fitin: f, n: done.length, ...c });
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.start - b.start));
}
export const FIT_OFTEN_WORDS = { once: 'once a week', two: '2–3 times a week', most: 'most days' };
/** 1, 1 → 'once a week'; 1, 2 → 'once or twice a week'; 2, 3 → '2–3 times a week'; 5, 7 → 'most days'. `short` drops
    'a week' ('2–3 times', 'once or twice'). */
export function oftenWords(lo, hi, { short = false } = {}) {
  const wk = short ? '' : ' a week';
  if (lo >= 7) return 'every day';
  if (lo >= 5) return 'most days';
  const n = (x) => (x === 1 ? 'once' : x === 2 ? 'twice' : x + ' times');
  if (lo === hi) return n(lo) + wk;
  if (lo === 1 && hi === 2) return 'once or twice' + wk;
  return lo + '–' + hi + ' times' + wk;
}
const FIT_LEN = { 30: 'half an hour', 60: 'an hour', 90: 'an hour and a half', 120: 'two hours', 180: 'three hours' };
export const fitLen = (m) => FIT_LEN[m] || (m % 60 ? m + ' minutes' : m / 60 + ' hours');
const DAY_SINGLE = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const orList = (xs) => (xs.length < 2 ? xs.join('') : xs.slice(0, -1).join(', ') + ' or ' + xs[xs.length - 1]);
/** Its days and time of day in words: 'weekday afternoons', 'Friday or Saturday evenings', 'on weekdays', 'at weekends'
    (gb) / 'on weekends', 'on Fridays or Saturdays', 'in the afternoons', 'in the mornings or evenings', or ''. */
export function fitWhen(f, { gb = false } = {}) {
  if (!ranged(f)) {
    const ks = ['day', 'evening', 'weekend'].filter((k) => ((f && f.fits) || []).includes(k));
    const W = { day: 'in the daytime', evening: 'in the evenings', weekend: gb ? 'at weekends' : 'on weekends' };
    return ks.length && ks.length < 3 ? ks.map((k) => W[k]).join(' or ') : '';
  }
  const d = [...new Set(f.days || [])].sort((a, b) => a - b);
  const ps = PART_KEYS.filter((p) => (f.parts || []).includes(p));
  const partW = ps.length && ps.length < 3 ? orList(ps.map((p) => p + 's')) : '';
  const dk = !d.length || d.length === 7 ? '' : d.join() === '0,1,2,3,4' ? 'weekday' : d.join() === '5,6' ? 'weekend' : 'list';
  if (dk && partW) return (dk === 'list' ? orList(d.map((x) => DAY_SINGLE[x])) : dk) + ' ' + partW;
  if (dk === 'weekday') return 'on weekdays';
  if (dk === 'weekend') return gb ? 'at weekends' : 'on weekends';
  if (dk === 'list') return 'on ' + orList(d.map((x) => PLURAL_DAYS[x]));
  return partW ? 'in the ' + partW : '';
}
/** Who it's with: 'With Mira', 'With Mira and Ada', 'You and Ada', 'Just you'. `kids`: the children's names. */
export function withWords({ together, partnerName, kids = [] }) {
  const both = together && partnerName;
  if (both) return 'With ' + andList([partnerName, ...kids]);
  if (kids.length) return 'You and ' + andList(kids);
  return 'Just you';
}
/** Read back above Save: "With Mira, once or twice a week for two hours, Friday or Saturday evenings. We'll suggest it
    when you're both free." `gb` says "at weekends", everyone else "on weekends". */
export function fitLine(f, { partnerName, gb = false, kids = [] } = {}) {
  if (!f) return '';
  const [lo, hi] = fitRange(f);
  const when = fitWhen(f, { gb });
  const both = f.together && partnerName;
  return withWords({ together: f.together, partnerName, kids }) + ', ' + oftenWords(lo, hi) + ' for ' + fitLen(f.minutes || 60) + (when ? ', ' + when : '')
    + '. We’ll suggest it when you’re ' + (both ? 'both ' : '') + 'free.';
}
/** On a row: '2–3 times a week for an hour, weekday afternoons'. */
export function fitSummary(f, { gb = false } = {}) {
  const [lo, hi] = fitRange(f);
  const when = fitWhen(f, { gb });
  return lowerToUpper(oftenWords(lo, hi)) + ' for ' + fitLen(f.minutes || 60) + (when ? ', ' + when : '');
}
/** Under the grid: '2–3 times · weekday afternoons'. */
export function fitShort(f, { gb = false } = {}) {
  const [lo, hi] = fitRange(f);
  const when = fitWhen(f, { gb }).replace(/^(on|in the|at) /, '');
  return oftenWords(lo, hi, { short: true }) + (when ? ' · ' + when : '');
}
const lowerToUpper = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
/** How it's going this week: "0 of 2–3 this week", "Once a week", "Most days · 2 so far this week". */
export function fitProgress(f, n) {
  const [lo, hi] = fitRange(f);
  if (hi <= 1) return 'Once a week';
  if (lo >= 5) return n ? 'Most days · ' + n + ' so far this week' : 'Most days';
  return n + ' of ' + (lo === hi ? lo : lo + '–' + hi) + ' this week';
}
/** "You're both free 1–6pm" / "You're both free all day" / "You're free from 7pm". */
export function fitFreeWords(s, together = true) {
  return (together ? 'You’re both free ' : 'You’re free ') + (s.all ? 'all day' : s.to >= WAKE_END - 15 ? 'from ' + ampm(s.from) : timeRange(s.from, s.to));
}

/* ── Ideas for your free time: the carousel at the top of Home (Ben, 29 Oct: "say yes, no or leave them") ── */
/** The first evening (or whole free day) in the seven from `today` worth a date: two hours or more together,
    starting between 5pm and 8pm, nothing planned; none once you've said no to one. `segs` is freeSegs().
    → { date, from, all } | null */
export function dateNight(segs, today, no = []) {
  if (no.some((k) => k.startsWith('date:'))) return null;   // a no to a date night is a no for the week
  for (let i = 0; i < 7; i++) {
    const date = addDays(today, i);
    const s = (segs || {})[date] || [];
    const all = i > 0 && s.length === 1 && s[0][0] <= WAKE_START + 60 && s[0][1] >= WAKE_END - 15;
    const eve = intersect(s, [[17 * 60, WAKE_END]]).find(([a, b]) => b - a >= 120 && a <= 20 * 60);   // two hours, from 8pm at the latest
    if (all || eve) return { date, from: all ? null : eve[0], all };
  }
  return null;
}
/** Everything to suggest for the week, by day: the things you fit in, each day's new episode, dinner or plan
    (suggestions()), a fine day outside and a date night. Gifts stay on their day (they aren't time to fill).
    `no`: the keys you've said no to this week. → [{ key, kind, date, … }] */
/* What you'd like more of (setup's "And the two of you?", yours and your partner's together) steers the ideas: the
   first idea of a kind you want leads the carousel, and Trips away turns a free weekend into "A weekend away?".
   Friends, together has no idea of its own yet. */
export const MORE_KINDS = { 'Nights out': ['date'], 'Time outside': ['outdoor'], 'Trying new things': ['date', 'outdoor'], 'Quiet nights in': ['show', 'dinner'], 'Trips away': ['trip'] };
/** Both of yours → the kinds of idea to lead with. */
export function moreKinds(...lists) {
  const out = new Set();
  for (const l of lists) for (const m of l || []) for (const k of MORE_KINDS[m] || []) out.add(k);
  return out;
}
export function weekIdeas({ sugg = {}, fits = [], outdoor = null, date = null, no = [], more = [] }) {
  const want = more instanceof Set ? more : moreKinds(more);
  const out = fits.map((f) => ({ ...f, kind: 'fitin' }));
  // Trips away: a Saturday and Sunday you're both wholly free, with nothing planned, is one idea — the weekend
  const trip = want.has('trip') && Object.keys(sugg).sort().find((d) => weekday(d) === 5 && sugg[d].kind === 'plan'
    && sugg[addDays(d, 1)] && sugg[addDays(d, 1)].kind === 'plan' && !no.includes('trip:' + d));
  if (trip) out.push({ key: 'trip:' + trip, kind: 'trip', date: trip, to: addDays(trip, 1) });
  for (const d of Object.keys(sugg)) {
    const s = sugg[d];
    if (s.kind === 'show') out.push({ key: 'show:' + s.episode.id + ':' + d, kind: 'show', date: d, episode: s.episode });
    else if (s.kind === 'dinner') out.push({ key: 'dinner:' + s.recipe.id, kind: 'dinner', date: d, recipe: s.recipe });
    else if (s.kind === 'plan' && !(trip && (d === trip || d === addDays(trip, 1)))) out.push({ key: 'plan:' + d, kind: 'plan', date: d });
  }
  if (outdoor) out.push({ ...outdoor, key: 'outdoor:' + outdoor.date, kind: 'outdoor' });
  if (date) out.push({ ...date, key: 'date:' + date.date, kind: 'date' });
  const ORDER = ['trip', 'outdoor', 'fitin', 'date', 'show', 'dinner', 'plan'];
  const list = out.filter((x) => !no.includes(x.key))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind)));
  const lead = list.findIndex((x) => want.has(x.kind));
  if (lead > 0) list.unshift(list.splice(lead, 1)[0]);
  return list;
}

/* ── Asking (0023 for a show tonight, 0045 for a date) ── */
/** A plan your partner made for you both that waits on your answer: a show (not a weekly one) or a date from
    Courted. Leaving a show counts as yes; a date asks until you say. */
export function isAsk(p, me) {
  if (!p || (p.status && p.status !== 'planned') || p.who === 'me' || p.agreed_by || !p.created_by || p.created_by === me) return false;
  return (p.source === 'curated' && !p.release) || p.source === 'courted';
}
/** Whether a plan's time has come, so its ✓ ("We did it") can show: a day before today, an all-day plan today, a
    timed one from its start, an untimed one (an evening: a show, a dinner out) from 6pm. `date` is the first day
    (a trip's start); `now` is minutes since midnight. Before then a tick reads as "set it up" (feedback, 31 Oct:
    someone ticked MobLand to plan it, saw nothing change, and undid it). */
export function planStarted(p, { today, now, date = p && p.on_date }) {
  if (!p || !date || date > today) return false;
  if (date < today || p.all_day) return true;
  const s = mins(p.start_time);
  return now >= (s != null ? s : 18 * 60);
}
/** What a plan's row says beside its time: Every week, Done, Just you / Just Olya, "Olya's up for it" (yours to
    answer), You're both in, Waiting for Olya (a show or a date you asked about), Olya added it — or nothing.
    `partnerName` is null in a home of one; `done` is the whole group's (a trip's days). */
export function planNote(p, { me, partnerName, done = !!p && p.status === 'done' }) {
  if (!p) return '';
  const mine = !p.created_by || p.created_by === me;
  if (p.regular) return 'Every week';
  if (done) return 'Done';
  if (p.who === 'me') return mine ? 'Just you' : 'Just ' + partnerName;
  if (isAsk(p, me)) return partnerName + '’s up for it';
  if (p.agreed_by) return 'You’re both in';
  const asks = p.source === 'courted' || (p.source === 'curated' && !p.release);
  if (asks && mine && partnerName) return 'Waiting for ' + partnerName;
  if (!mine) return partnerName + ' added it';
  return '';
}
/** Courted's dates with an answer to give or to hear, from today: `ask` (your partner's, for you to answer),
    `agreed` (yours, they're in — until you've seen it), `declined` (yours, they can't make it — until cleared).
    `plans` are the week's rows, dropped ones too. → [{ key, kind, date, plan }] by date */
export function dateCards(plans, { me, today, seen = [], cleared = [] }) {
  const out = [];
  for (const p of plans || []) {
    if (p.source !== 'courted' || p.who === 'me' || p.on_date < today) continue;
    const mine = !p.created_by || p.created_by === me;
    const st = p.status || 'planned';
    if (st === 'planned' && isAsk(p, me)) out.push({ key: 'ask:' + p.id, kind: 'ask', date: p.on_date, plan: p });
    else if (st === 'planned' && mine && p.agreed_by && !seen.includes(p.id)) out.push({ key: 'agreed:' + p.id, kind: 'agreed', date: p.on_date, plan: p });
    else if (st === 'dropped' && mine && p.declined_by && !cleared.includes(p.id)) out.push({ key: 'declined:' + p.id, kind: 'declined', date: p.on_date, plan: p });
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}
/** A date's name mid-sentence: a place's name as it is ("Larkin & Vine"), a kind in lower case ("dinner out").
    `named`: the title is a place's (our catalogue's, or Google's live). */
export function dateWhat(p, named) {
  const t = String((p && p.title) || 'a date');
  return named || (p && p.ref && p.ref.named) ? t : t.charAt(0).toLowerCase() + t.slice(1);
}
/** When a date is, in words: "tonight" (from 5pm, or no time), "today", "tomorrow", "on Friday"; with its time,
    "on Friday at 7pm". */
export function dateWhen(p, today, { at = true } = {}) {
  const s = mins(p.start_time);
  const day = p.on_date === today ? (s == null || s >= 17 * 60 ? 'tonight' : 'today') : p.on_date === addDays(today, 1) ? 'tomorrow'
    : 'on ' + ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][weekday(p.on_date)];
  return day + (at && s != null ? ' at ' + ampm(s) : '');
}

/** Hours to the nearest half: 270 → '4½h', 60 → '1h', 30 → '½h', 0 → '0h'. */
export function fmtHalf(m) {
  const r = Math.round(Math.max(0, m) / 30) / 2, w = Math.floor(r), half = r - w >= 0.5;
  return (w || !half ? String(w) : '') + (half ? '½' : '') + 'h';
}
const DURATION = { 30: 'Half an hour', 60: 'An hour', 90: 'An hour and a half' };

/** Where the plans sit on a day's bar: timed ones where they are; untimed ones (they count two
    hours, or a show's length) at the start of the evening you're both free. */
export function plannedSegs(day, plans) {
  const timed = [], segs = [];
  let untimed = 0;
  for (const p of plans) {
    if (p.on_date !== day.date || p.status === 'dropped' || p.who === 'me') continue;
    if (p.all_day) { timed.push([WAKE_START, WAKE_END]); continue; }
    const s = mins(p.start_time), e = mins(p.end_time);
    if (s != null) timed.push([s, e != null && e > s ? e : s + (p.minutes || 120)]);
    else untimed += p.minutes || 120;
  }
  segs.push(...intersect(day.together, timed));
  if (untimed) {
    const free = intersect(day.together, [[18 * 60, WAKE_END]]).concat(day.together);
    const left = free.find(([a, b]) => b - a > 0);
    if (left) segs.push([left[0], Math.min(left[1], left[0] + untimed)]);
  }
  return union(segs);
}

/** The evenings you're both free with nothing planned, for Curated's "An evening" (src/core/week-free.js):
    per day, the free stretch that runs into the evening — { date, from, to, all } — an hour or more.
    `all` when the whole day is free; today starts no earlier than `now` (minutes), on the half hour. */
export function freeEvenings(days, plans, { today = null, now = null } = {}) {
  const out = [];
  for (const d of days) {
    if (!d.partner) continue;
    const planned = plannedSegs(d, plans);
    let free = minus(union(d.together), planned);
    const from = d.from == null ? WAKE_START : d.from;
    const all = d.date !== today && !planned.length && total(free) >= (d.to ?? WAKE_END) - from - 30;
    if (d.date === today && now != null) free = intersect(free, [[Math.ceil(now / 30) * 30, WAKE_END]]);
    const eve = free.filter(([a, b]) => b >= 21 * 60 && b - a >= 60).pop();
    if (eve) out.push({ date: d.date, from: eve[0], to: eve[1], all });
  }
  return out;
}

/** All the time you're both free with nothing planned, per day — { date: [[a, b]] } in minutes — for Courted's
    "Saturday looks good for a hike" (three hours of daylight). Today starts no earlier than `now`. */
export function freeSegs(days, plans, { today = null, now = null, couple = false } = {}) {
  const out = {};
  for (const d of days) {
    if (!d.partner) continue;
    // couple: just the two of you (the children at school, in bed or with someone else)
    let free = minus(union(couple && d.couple ? d.couple : d.together), plannedSegs(d, plans));
    if (d.date === today && now != null) free = intersect(free, [[Math.ceil(now / 30) * 30, WAKE_END]]);
    out[d.date] = free.filter(([a, b]) => b > a);
  }
  return out;
}

/** What an evening holds, for Catered's Add to a day: "You’re both home from 6:30pm", "You’re out until
    10pm", "You’re both free all day". From the together time and who's out, not from what's planned
    (a dinner already on the day doesn't make the evening busy). → { line, both, all?, from? } or null (`from`: minutes,
    for Courted's Perfect days). */
export function eveningLine(d, partnerName) {
  const EVE = [[17 * 60, WAKE_END]];
  const from = d.from == null ? WAKE_START : d.from;
  if (d.partner) {
    const t = union(d.together);
    if (total(t) >= (d.to ?? WAKE_END) - from - 30) return { line: 'You’re both free all day', both: true, all: true };
    // Home together in time for dinner: from 8:30pm at the latest, for an hour or more.
    const eve = intersect(t, EVE).filter(([a, b]) => b >= 21 * 60 && b - a >= 60 && a <= 20 * 60 + 30).pop();
    if (eve) return { line: 'You’re both home from ' + ampm(eve[0]), both: true, from: eve[0] };
  }
  const until = (p, key) => { const x = p ? intersect(union(p[key] || []), EVE) : []; return x.length ? x[x.length - 1][1] : null; };
  const me = until(d.me, 'out'), them = until(d.partner, 'out');
  const name = partnerName || 'They';
  if (me && them) return { line: me === them ? 'You’re both out until ' + ampm(me) : 'You’re out until ' + ampm(me) + ', ' + name + ' until ' + ampm(them), both: false };
  if (me) return { line: 'You’re out until ' + ampm(me), both: false };
  if (them) return { line: name + '’s out until ' + ampm(them), both: false };
  const mb = until(d.me, 'busy'), tb = until(d.partner, 'busy');
  if (mb && (!tb || mb >= tb)) return { line: 'You’re busy until ' + ampm(mb), both: false };
  if (tb) return { line: name + '’s busy until ' + ampm(tb), both: false };
  if (!d.partner) return { line: 'You’re free this evening', both: false };
  return null;
}

/** The together hours, on purpose and counted hours over `days` (computeWeek's days). */
export function aheadTotals(days) {
  const out = { together: 0, onPurpose: 0, awake: 0, taken: 0, family: 0, couple: 0 };
  for (const d of days) {
    if (!d.partner) continue;
    out.together += d.togetherMins; out.onPurpose += d.onPurposeMins; out.awake += (d.to ?? WAKE_END) - d.from; out.taken += d.takenMins || 0;
    out.family += d.familyMins || 0; out.couple += d.coupleMins != null ? d.coupleMins : d.togetherMins;
  }
  out.apart = out.awake - out.together - out.taken;
  return out;
}

/** A day's together time in words. */
export function dayHeadline(day) {
  if (!day.partner) return null;
  const t = union(day.together), from = day.from == null ? WAKE_START : day.from, end = day.to ?? WAKE_END;
  const span = end - from, m = total(t);
  if (!m) return 'Apart all day';
  if (m >= span - 30) return 'Free all day';
  if (t.length === 1) {
    const [a, b] = t[0];
    if (b >= end - 15) { const k = Math.round(m / 30) * 30; return m <= 90 && DURATION[k] ? DURATION[k] + ', from ' + ampm(a) : 'Free from ' + ampm(a); }
    if (a <= from + 15) return 'Free until ' + ampm(b);
    return 'Free ' + ampm(a) + ' to ' + ampm(b);
  }
  if (m >= span / 2) return 'Free most of the day';
  const last = t[t.length - 1];
  if (last[1] >= end - 15 && last[1] - last[0] >= 120) return 'Free from ' + ampm(last[0]);
  return 'A few hours together';
}

const firstPart = (name) => String(name || '').split(',')[0].trim();
const lowerFirst = (s) => (s && s.length > 1 && s[1] === s[1].toLowerCase() ? s[0].toLowerCase() + s.slice(1) : s);
const andList = (xs) => (xs.length < 2 ? xs.join('') : xs.slice(0, -1).join(', ') + ' and ' + xs[xs.length - 1]);

/** What fills one person's day, in a phrase ("You're in Markham, then pool"), or null. `me` picks You / the name. */
export function personPhrase(person, date, pDay, { name, me = false } = {}) {
  if (!person || !pDay) return null;
  const wd = weekday(date);
  const ov = (person.overrides || []).find((o) => o.on_date === date) || null;
  const are = me ? 'You’re' : name + '’s', have = me ? 'You’ve got' : name + '’s got';
  if (ov && ov.away_all_day) return are + ' away';
  if (pDay.vacation || pDay.holiday) return are + ' off';
  let work = null;
  if (pDay.kind === 'away') {
    const ids = (ov && ov.place_ids) || usualDay(person, wd).placeIds;
    const names = [...new Set((person.places || []).filter((pl) => ids.includes(pl.id) && pl.kind !== 'home').map((pl) => firstPart(pl.name)).filter(Boolean))];
    work = names.length ? 'in ' + andList(names) : 'out';
  } else if (pDay.kind === 'home_busy') work = 'working from home';
  // The regulars that happen today (the check-in can replace the evening's), out and at home.
  const evening = ov && ov.evening_kind;
  const regs = (person.regulars || []).filter((r) => (r.days || []).includes(wd) && !r.together && mins(r.start_time) != null && !(evening && mins(r.start_time) >= EVENING_START));
  const outs = regs.filter((r) => r.away !== false && r.kind !== 'home').map((r) => lowerFirst(String(r.name || '').trim())).filter(Boolean);
  if (evening === 'out') outs.push('a night out');
  const home = regs.filter((r) => r.kind === 'home' && mins(r.end_time) != null).sort((a, b) => mins(a.end_time) - mins(b.end_time)).pop();
  const homeBit = home ? lowerFirst(String(home.name || '').trim() || 'running the home') + ' until ' + ampm(mins(home.end_time)) : null;
  if (work) return are + ' ' + work + (outs.length ? ', then ' + andList(outs) : '') + (homeBit ? ', and ' + homeBit : '');
  if (outs.length) return have + ' ' + andList(outs) + (homeBit ? ', and ' + (me ? 'you’re ' : 'is ') + homeBit : '');
  if (homeBit) return are + ' ' + homeBit;
  return null;
}

/** The line under a day: "You're in Oakville. Olya's cooking until 6:30pm." — or, with nothing in
    either week and no events, "Nothing in either calendar." */
export function dayLine(day, me, partner, partnerName, { events = 0 } = {}) {
  const a = personPhrase(me, day.date, day.me, { me: true });
  const b = partner && day.partner ? personPhrase(partner, day.date, day.partner, { name: partnerName || 'They' }) : null;
  const xs = [a, b].filter(Boolean);
  if (xs.length) return xs.join('. ') + '.';
  return events ? '' : 'Nothing in either calendar.';
}

/** One thing to do with each day of the week ahead, most useful first: a gift that's due, a new
    episode of a show you're both on, a plan for a day that's wholly free, a dinner when you're
    both home for it, or a plan for a day that's mostly free. Each recipe is offered once. `no`: the keys you've
    said no to on Home's ideas ('show:<id>:<date>', 'dinner:<recipe>', 'plan:<date>'). → { [date]: { kind, ... } } */
export function suggestions({ days, flags = [], episodes = [], plans = [], recipes = [], dinners = new Set(), skip = [], no = [] }) {
  const out = {};
  const pool = recipes.filter((r) => !no.includes('dinner:' + r.id));
  const planned = (date) => plans.filter((p) => p.on_date === date && p.status !== 'dropped' && p.who !== 'me');
  for (const d of days) {
    if (!d.partner) continue;
    const gift = flags.find((f) => f.date === d.date && !f.member && f.action !== 'bought');
    if (gift) { out[d.date] = { kind: 'gift', flag: gift }; continue; }
    const ep = episodes.find((e) => e.date === d.date && !e.releaseNight && !skip.includes(e.id) && !no.includes('show:' + e.id + ':' + d.date)
      && !planned(d.date).some((p) => p.ref && Number(p.ref.tmdb_id) === e.id));
    const evening = total(intersect(d.together, [[18 * 60, WAKE_END]]));
    if (ep && evening >= Math.min(ep.minutes || 60, 60)) { out[d.date] = { kind: 'show', episode: ep }; continue; }
    // A whole day free with nothing planned: plan something first (a dinner can wait for another day).
    const nothing = !planned(d.date).length && !no.includes('plan:' + d.date);
    const span = (d.to ?? WAKE_END) - (d.from == null ? WAKE_START : d.from);
    const allTogether = total(d.together);   // meals included: a whole free day is still a whole free day
    if (nothing && allTogether >= span - 30) { out[d.date] = { kind: 'plan' }; continue; }
    const dinnerTime = total(intersect(d.together, [[18 * 60 + 30, 20 * 60 + 30]]));
    const busyEvening = planned(d.date).some((p) => p.all_day || (mins(p.start_time) != null && mins(p.start_time) >= 17 * 60));
    if (dinnerTime >= 90 && !dinners.has(d.date) && !busyEvening && pool.length) { out[d.date] = { kind: 'dinner', recipe: pool.shift() }; continue; }
    if (nothing && allTogether >= 8 * 60) out[d.date] = { kind: 'plan' };
  }
  return out;
}

/** Tonight, in a line: what's planned ("Risotto, then MobLand."), or whether you're both free. */
export function tonightTitle({ day, titles = [], partnerName }) {
  if (titles.length) return (titles.length > 2 ? titles.slice(0, 2).join(', ') + ' and more' : titles.join(', then ')) + '.';
  if (!day || !day.partner) return 'Tonight';
  const eve = intersect(day.together, [[18 * 60, WAKE_END]]);
  const m = total(eve);
  if (m >= 120) return eve[0][0] <= 18 * 60 + 30 ? 'You’re both free.' : 'You’re both free from ' + ampm(eve[0][0]) + '.';
  if (m > 0) return 'Free from ' + ampm(eve[eve.length - 1][0]) + '.';
  const meOut = total(intersect(union([...day.me.out, ...day.me.busy]), [[18 * 60, WAKE_END]])) > 0;
  const themOut = total(intersect(union([...day.partner.out, ...day.partner.busy]), [[18 * 60, WAKE_END]])) > 0;
  if (meOut && themOut) return 'You’re both busy tonight.';
  return meOut ? 'You’re busy tonight.' : (partnerName || 'They') + '’s busy tonight.';
}

// ── Which country to search first ───────────────────────────────────────────
/* The phone's time zone says the country far more reliably than its language
   (a UK-English phone in Toronto still says America/Toronto). Only zones that
   belong to one country are listed; anything else returns null. */
const TZ_CA = ['Toronto', 'Montreal', 'Vancouver', 'Edmonton', 'Winnipeg', 'Halifax', 'St_Johns', 'Regina', 'Moncton', 'Glace_Bay',
  'Goose_Bay', 'Whitehorse', 'Dawson', 'Dawson_Creek', 'Fort_Nelson', 'Creston', 'Yellowknife', 'Inuvik', 'Iqaluit', 'Rankin_Inlet',
  'Resolute', 'Cambridge_Bay', 'Swift_Current', 'Atikokan', 'Blanc-Sablon', 'Nipigon', 'Thunder_Bay', 'Rainy_River', 'Pangnirtung'];
const TZ_US = ['New_York', 'Chicago', 'Denver', 'Los_Angeles', 'Phoenix', 'Anchorage', 'Detroit', 'Boise', 'Juneau', 'Sitka', 'Nome',
  'Yakutat', 'Metlakatla', 'Adak', 'Menominee'];
export function regionFromTimeZone(tz) {
  if (!tz) return null;
  const [area, city, sub] = String(tz).split('/');
  if (area === 'America' && TZ_CA.includes(city) && !sub) return 'ca';
  if (area === 'America' && (TZ_US.includes(city) || city === 'Indiana' || city === 'Kentucky' || city === 'North_Dakota')) return 'us';
  if (tz === 'Pacific/Honolulu') return 'us';
  if (tz === 'Europe/London' || tz === 'Europe/Belfast') return 'gb';
  if (tz === 'Europe/Dublin') return 'ie';
  if (area === 'Australia') return 'au';
  if (tz === 'Pacific/Auckland' || tz === 'Pacific/Chatham') return 'nz';
  return null;
}
/* The country to rank first, most trusted first: the household's own (set in
   Curated); the phone's time zone (set from where the phone is, and immune to
   VPNs and iCloud Private Relay); where the connection comes from; the language. */
export function searchRegion({ household, ip, timeZone, language } = {}) {
  const lang = /-([A-Za-z]{2})\b/.exec(language || '');
  const pick = household || regionFromTimeZone(timeZone) || ip || (lang && lang[1]);
  return pick ? String(pick).toLowerCase() : null;
}

/** Chores' − and + in setup (Ben, 28 Oct: "I only do 1 hour of chores a week"): an hour at a time up to 10, then five
    at a time, to 80. */
export const choreStep = (h, dir) => (dir > 0 ? (h < 10 ? h + 1 : Math.min(80, h + 5)) : (h > 10 ? Math.max(10, h - 5) : Math.max(0, h - 1)));

/** Where a plan or an event is, as its line on Home (the canvas's WhereLinks boards). A place from Google Maps opens
    Maps at that place; an address shows its name up to the first comma and opens a Maps search for all of it; a web
    link shows the site's name, without www. or bid. ("waddingtons.ca"), and opens it; a video call names the service
    (Zoom, Google Meet, Teams…) and opens the call. `place`: { id?, name, address? }, or a calendar location's text as
    { name }. → { kind: 'place' | 'link' | 'call', label, href } | null */
const CALLS = [[/(^|\.)zoom\.us$/, 'Zoom'], [/^meet\.google\.com$/, 'Google Meet'], [/^teams\.(microsoft|live)\.com$/, 'Teams'], [/(^|\.)webex\.com$/, 'Webex'],
  [/^whereby\.com$/, 'Whereby'], [/^facetime\.apple\.com$/, 'FaceTime'], [/(^|\.)gotomeeting\.com$/, 'GoTo Meeting']];
export function whereOf(place) {
  if (!place || !place.name) return null;
  const text = String(place.name).trim();
  const url = !place.id && text.match(/https?:\/\/[^\s<>"')]+/i);
  if (url) {
    let host = '';
    try { host = new URL(url[0]).hostname.toLowerCase(); } catch (e) { host = ''; }
    if (host) {
      const call = CALLS.find(([re]) => re.test(host));
      if (call) return { kind: 'call', label: call[1], href: url[0] };
      return { kind: 'link', label: host.replace(/^(www|bid|m)\./, ''), href: url[0] };
    }
  }
  const q = [text, place.address].filter(Boolean).join(', ');
  const href = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q) + (place.id ? '&query_place_id=' + encodeURIComponent(place.id) : '');
  return { kind: 'place', label: place.id ? text : text.split(',')[0].trim() || text, href };
}

/* "Olya joined you" (joined.jsx): once, for a partner who joined after you, in the last fortnight */
const JOINED_FOR = 14 * 86400000;
/** Whether to show it: your partner joined your home after you, recently, and you haven't seen it since. */
export function joinedDue(hh, now = Date.now()) {
  if (!hh || !hh.partnerId || !hh.partnerJoinedAt || !hh.meJoinedAt) return false;
  const them = Date.parse(hh.partnerJoinedAt), me = Date.parse(hh.meJoinedAt);
  if (!(them > me) || now - them > JOINED_FOR) return false;
  const seen = hh.profile && hh.profile.partner_joined_seen_at ? Date.parse(hh.profile.partner_joined_seen_at) : 0;
  return !(seen >= them);
}

/** "Just now", "Today", "Yesterday", "On Monday". */
export function joinedWhen(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (now - t < 3600000) return 'Just now';
  const day = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
  const days = Math.round((day(now) - day(t)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return 'On ' + new Date(t).toLocaleDateString('en', { weekday: 'long' });
}

